import styles from "../styles.module.scss";
import {useAppData} from "../../context/Contexts";
import {useQuery} from "@tanstack/react-query";
import React, {useState, useEffect, useRef} from "react";
import fetchCheck from "../../services/CheckService";
import { fetchVehicleAvailability, fetchVehicleAvailabilityWithTrimCab } from "../../services/VehicleAvailabilityService";
import { buildBiz2Coverages } from "../../helpers/availabilityUtils";
import {
    preloadModelsForYear,
    awaitMakesSeed,
    awaitModelsSeed,
    awaitYearsSeed,
    getSeedFromMemory,
} from "../../services/SeedPrefetchService";
import useCustomProductQuery from "../../services/CustomProductService";
import {getApiBase, getTrims, MODEL_SORT_ORDER, slugify, withPartNumberSkuQuery} from "../../helpers/helpers";
import { withIDBFallback, IDB_STORES, IDB_TTL } from "../../services/IDBCacheService";
import SuperOffer from "../SuperOffer";
import VehicleTypeahead from "../VehicleTypeahead/VehicleTypeahead";
import { TypeaheadVehicle } from "../../services/VehicleTypeaheadService";

function getDefaultModel(
    currentAppData,
    model: string | undefined | null,
    models,
) {
    let model1 = model;
    if (currentAppData?.modelTemp) {
        model1 = currentAppData?.modelTemp;
    }
    if (window.scs_force_custom) {
        return "";
    }
    //console.log("model1:", model1);
    return model1;

    if (model1 === null || model1 === undefined) {
        return "";
    }
    if (!(models && models.data)) {
        return "";
    }
    if (typeof models.data[model1] !== "undefined") {
        return model1;
    }
    return "";
}

const visuallyHiddenStyle: React.CSSProperties = {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
};

export default function Step1New(props) {
    const {currentAppData, setCurrentAppData} = useAppData();
    const [disabledMakes, setDisabledMakes] = useState<boolean>(true);
    const [disabledModels, setDisabledModels] = useState<boolean>(true);
    const [showTrims, setShowTrims] = useState<boolean>(false);
    const [trims, setTrims] = useState<object>({});
    const [canMoveToStep2, setCanMoveToStep2] = useState<boolean>(false);
    // Tracks current step without stale closure issues; updated every render.
    const currentStepRef = useRef<number>(1);
    currentStepRef.current = currentAppData?.step ?? 1;
    // Tracks showTrimCab for setTimeout closures that would otherwise be stale.
    const showTrimCabRef = useRef<boolean>(false);
    // True while a typeahead pick (year+make+model+trim+cab set together) is being
    // applied. The model-change effect below normally resets trim/cab whenever
    // model changes, on the assumption trim/cab need to be re-picked for the new
    // model — but a typeahead selection already supplies a matching trim/cab for
    // that exact model, so the reset must be skipped for this one pass.
    const typeaheadSelectionRef = useRef<boolean>(false);

    const {
        custom_status,
        custom_data,
        custom_error,
        custom_isFetching,
        custom_refetch,
    } = useCustomProductQuery();

    // Fetch availability API as soon as year+make+model are known so we can decide
    // whether to show trim/cab (no questions) or skip them (configurator questions present).
    const availYear = currentAppData?.year;
    const availMake = currentAppData?.make;
    const availModel = currentAppData?.model;
    const availEnabled = !!(availYear && availMake && availModel) && window.scs_vehicle_configurator === true;

    const { data: availabilityData, isLoading: availabilityLoading } = useQuery({
        queryKey: ['vehicle-availability', availYear, availMake, availModel],
        queryFn: () => fetchVehicleAvailability(availYear!, availMake!, availModel!),
        enabled: availEnabled,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    const availConfigurator = Array.isArray(availabilityData?.data?.configurator) ? availabilityData.data.configurator : [];
    const hasConfiguratorQuestions = availConfigurator.length > 0 &&
        Array.isArray(availConfigurator[0]?.questions) &&
        availConfigurator[0].questions.length > 0;
    const showTrimCab = availConfigurator.length > 0 && availConfigurator[0]?.show_trim_cab === true;
    showTrimCabRef.current = showTrimCab;

    const displayMode = availConfigurator[0]?.display_mode ?? 'icon_path';

    // Block StepNav from advancing while the availability check is in flight
    useEffect(() => {
        if (!availEnabled) return;
        setCurrentAppData(prev => ({ ...prev, biz2_availability_loading: availabilityLoading }));
    }, [availabilityLoading, availEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep configurator_display_mode in context in sync with the availability response.
    useEffect(() => {
        if (!availEnabled) return;
        setCurrentAppData(prev => ({ ...prev, configurator_display_mode: displayMode }));
    }, [availabilityData]); // eslint-disable-line react-hooks/exhaustive-deps

    // When configurator questions are present, either suppress trim/cab or require them.
    useEffect(() => {
        if (availabilityLoading || !availEnabled) return;
        if (!hasConfiguratorQuestions) return;

        if (showTrimCab) {
            // show_trim_cab: force trim field visible and block Next until user selects a trim.
            // This overrides the else-branch in runHandleModelChange that may have set
            // has_trims:false when trims2 data wasn't in the IDB seed yet.
            setShowTrims(true);
            if (!currentAppData?.trim) {
                setCurrentAppData(prev => ({ ...prev, has_trims: true, check: undefined }));
            }
            return;
        }

        setShowTrims(false);
        setCurrentAppData(prev => ({ ...prev, has_trims: false, has_cabs: false }));
        // For vehicles with multiple trims, runCheckNow is never called automatically
        // (user would normally pick a trim first). We need a check result so StepNav
        // can evaluate availability and enable the Next button.
        if (!currentAppData?.check) {
            runCheckNow(currentAppData?.model, '', false);
        }
    }, [availabilityData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Advance to step 2 once BOTH questions are confirmed AND check is set.
    // Only auto-advance when show_trim_cab is false; when true the user must pick trim/cab first.
    // Depending on [check, availabilityData] means this fires after runHandleModelChange /
    // runCheckNow complete, so it runs after the later useEffects that reset step to 1,
    // not before them (which was the previous race condition).
    useEffect(() => {
        if (!hasConfiguratorQuestions || !availEnabled || showTrimCab) return;
        if (!currentAppData?.check) return;
        setCurrentAppData(prev => ({ ...prev, step: prev.step === 1 ? 2 : prev.step }));
    }, [currentAppData?.check, availabilityData]); // eslint-disable-line react-hooks/exhaustive-deps

    let type = "normal";
    if (window?.is_real_truck) {
        type = "realtruck";
    } else if (window?.scs_force_custom) {
        type = "custom";
    } else if (window?.scs_custom_endpoint) {
        type = "custom_endpoint";
    }

    const fetchYears = async () => {
        const year = currentAppData?.year;
        const make = currentAppData?.make;
        const model = currentAppData?.model;
        const trim = currentAppData?.trim;

        let query = `?type=${type}`;
        if (window.scs_use_optgroup) {
            query += `&with_sales=yes`;
        }
        if (make && model && year) {
            query += `&make=${make}&model=${model}&year=${year}&trim=${trim}`;
        }
        const urlYears = withPartNumberSkuQuery(`${getApiBase()}/api/v1/years` + query);
        try {
            const response = await fetch(urlYears, {
                mode: "cors",
            });
            if (!response.ok) throw new Error(`Years fetch failed: ${response.statusText}`);
            const data = await response.json();
            return data.years;
        } catch (err) {
            console.warn("[Step1New] fetchYears network failed, using seed fallback:", err);
            // Seed/IDB fallback path: read directly from the bundled JSON seed.
            // The seed generator currently only writes the unsuffixed `years_<type>` key,
            // even though the runtime builds `years_<type>_with_sales` when optgroup mode
            // is on. Try the suffixed key first, then fall back to the unsuffixed one.
            await awaitYearsSeed();
            const baseKey = `years_${type}`;
            const idbKey = `${baseKey}${window.scs_use_optgroup ? "_with_sales" : ""}`;
            const seedYears =
                getSeedFromMemory<string[]>(IDB_STORES.YEARS, idbKey) ??
                getSeedFromMemory<string[]>(IDB_STORES.YEARS, baseKey);
            if (Array.isArray(seedYears) && seedYears.length > 0) return seedYears;
            throw err;
        }
    };
    const fetchMakes = async () => {
        const year = currentAppData?.year;
        if (!year) return [];
        const withSales = !!window.scs_use_optgroup;
        const idbKey = `makes_${year}_${type}${withSales ? "_ws" : ""}`;

        // Ensure the makes seed has been written to memory/IDB before we query it.
        await awaitMakesSeed();

        const networkFetcher = async () => {
            let query = `?year=${year}&type=${type}`;
            if (withSales) query += `&with_sales=yes`;
            const urlMakes = withPartNumberSkuQuery(`${getApiBase()}/api/v1/years` + query);
            const response = await fetch(urlMakes, {
                mode: "cors",
            });
            if (!response.ok) throw new Error(`Makes fetch failed: ${response.statusText}`);
            const data = await response.json();
            return Array.isArray(data.makes) ? data.makes : Object.values(data.makes).flat();
        };

        let makes: string[] | undefined;
        try {
            makes = await withIDBFallback(
                IDB_STORES.MAKES,
                idbKey,
                IDB_TTL.MAKES,
                networkFetcher
            ) as string[];
        } catch (err) {
            console.warn(`[Step1New] fetchMakes network+IDB failed — key=${idbKey}`, err);
        }

        if (!Array.isArray(makes) || makes.length === 0) {
            // Seed only writes the unsuffixed `makes_<year>_<type>` key. Try the runtime
            // (possibly _ws-suffixed) key first, then fall back to the unsuffixed one.
            const baseKey = `makes_${year}_${type}`;
            const seedMakes =
                getSeedFromMemory<string[]>(IDB_STORES.MAKES, idbKey) ??
                (idbKey !== baseKey ? getSeedFromMemory<string[]>(IDB_STORES.MAKES, baseKey) : undefined);
            if (Array.isArray(seedMakes) && seedMakes.length > 0) {
                makes = seedMakes;
            }
        }

        if (!Array.isArray(makes) || makes.length === 0) {
            throw new Error(`No makes available for key=${idbKey}`);
        }

        if (withSales) return makes;
        return makes.slice().sort();
    };
    const fetchModels = async () => {
        const year = currentAppData?.year;
        const make = currentAppData?.make;
        if (!year) return [];
        if (!make) return [];
        const withSales = !!window.scs_use_optgroup;
        const idbKey = `models_${year}_${make}_${type}${withSales ? "_ws" : ""}`;

        // Ensure the models seed for this year has been written to IDB before we query it.
        await awaitModelsSeed(year);

        type ModelsEntry = { models: Record<string, string>; trims2: Record<string, unknown> | null | unknown[] };
        let raw: ModelsEntry | undefined;
        try {
            raw = await withIDBFallback(
                IDB_STORES.MODELS,
                idbKey,
                IDB_TTL.MODELS,
                async () => {
                    let query = `?year=${year}&make=${make}&type=${type}`;
                    if (withSales) query += `&with_sales=yes`;
                    const urlMakes = withPartNumberSkuQuery(`${getApiBase()}/api/v1/years` + query);
                    const response = await fetch(urlMakes, {
                        mode: "cors",
                    });
                    if (!response.ok) throw new Error(`Models fetch failed: ${response.statusText}`);
                    const data = await response.json();
                    return { models: data.models, trims2: data.trims2 ?? null };
                }
            ) as ModelsEntry;
        } catch (err) {
            console.warn(`[Step1New] fetchModels network+IDB failed — key=${idbKey}`, err);
        }

        if (!raw || !raw.models || Object.keys(raw.models).length === 0) {
            // Seed only writes the unsuffixed `models_<year>_<make>_<type>` key.
            const baseKey = `models_${year}_${make}_${type}`;
            const seedEntry =
                getSeedFromMemory<ModelsEntry>(IDB_STORES.MODELS, idbKey) ??
                (idbKey === baseKey ? undefined : getSeedFromMemory<ModelsEntry>(IDB_STORES.MODELS, baseKey));
            if (seedEntry && seedEntry.models && Object.keys(seedEntry.models).length > 0) {
                raw = { models: seedEntry.models, trims2: seedEntry.trims2 ?? null };
            }
        }

        if (!raw || !raw.models || Object.keys(raw.models).length === 0) {
            console.error(`[Step1New] fetchModels FAILED — key=${idbKey}`);
            throw new Error(`No models available for key=${idbKey}`);
        }

        // Keep raw trims2 for state — setTrims / currentAppData.trims always carry the raw value.
        setTrims(raw.trims2 ?? {});

        setCurrentAppData((prevState) => {
            return {...prevState, trims: raw.trims2 ?? null};
        });

        // Guard: only treat trims2 as a valid model→trim map when it is a non-empty, non-array object.
        // An empty array [] from the API/seed must NOT be treated as truthy here.
        const trims2Map = raw.trims2 && !Array.isArray(raw.trims2)
            ? raw.trims2 as Record<string, unknown>
            : null;

        if (trims2Map) {
            const queryParameters = new URLSearchParams(window.location.search);

            let model = getDefaultModel(
                currentAppData,
                window.sessionStorage.getItem("user_model") ?? null,
                {data: raw.models},
            );
            if (queryParameters.get("user_model")) {
                model = getDefaultModel(
                    currentAppData,
                    queryParameters.get("user_model") ?? null,
                    {data: raw.models},
                );
            }
            if (queryParameters.get("model")) {
                model = getDefaultModel(
                    currentAppData,
                    queryParameters.get("model") ?? null,
                    {data: raw.models},
                );
            }

            if (model && model != "") {
                if (Object.keys(trims2Map).length === 0) {
                    setCanMoveToStep2(true);
                } else {
                    if (typeof trims2Map[model] === "undefined") {
                        setCanMoveToStep2(true);
                    } else {
                        setCanMoveToStep2(false);
                    }
                }
            }
        }

        const modelValues = Object.values(raw.models ?? {});
        if (withSales) {
            return modelValues;
        }
        return modelValues.sort();
    };

    // useEffect(() => {
    //     if (currentAppData?.trims) {
    //         const queryParameters = new URLSearchParams(window.location.search)
    //
    //         let model = window.sessionStorage.getItem("user_model") ?? null;
    //         if (queryParameters.get("user_model")) {
    //             model = queryParameters.get("user_model") ?? null;
    //         }
    //         if (queryParameters.get("model")) {
    //             model = queryParameters.get("model") ?? null;
    //         }
    //
    //         const tt = getTrims(model);
    //         if (model && tt.length === 0) {
    //
    //             setTimeout(function () {
    //                 let newStep = 2;
    //                 setCurrentAppData(prevState => {
    //                     return {...prevState, step: newStep}
    //                 });
    //             }, 1500)
    //
    //         }
    //     }
    // }, [currentAppData?.trims]);

    useEffect(() => {
        const tt = getTrims(currentAppData?.trims, currentAppData?.model);
        // A trim may already be set for this model (e.g. picked via the YMM+trim+cab
        // typeahead, which resolves a complete vehicle before the trims2 catalog has
        // even finished loading from IDB/seed). Once that catalog arrives, don't blank
        // an already-valid trim just because the model turns out to have more than one.
        if (currentAppData?.trim && tt.includes(currentAppData.trim)) return;
        runCheckNow(
            currentAppData?.model,
            tt.length == 1 ? tt[0] : "",
            tt.length > 0,
        );
    }, [trims]);

    const yearsQuery = useQuery({queryKey: ["years", type], queryFn: fetchYears});

    const makesQuery = useQuery({
        queryKey: [`makes-${currentAppData?.year}`],
        queryFn: fetchMakes,
        retry: 0,
    });

    useEffect(() => {
        const makes = makesQuery.data as string[] | undefined;
        if (makes && makes.length > 0) setDisabledMakes(false);
    }, [makesQuery.data]);

    const modelsQuery = useQuery({
        queryKey: [`models-${currentAppData?.year}-${currentAppData?.make}`],
        queryFn: fetchModels,
        enabled: !!currentAppData?.make,
        retry: 0,
    });

    useEffect(() => {
        const models = modelsQuery.data as string[] | undefined;
        if (models && models.length > 0) setDisabledModels(false);
        else setDisabledModels(true);
    }, [modelsQuery.data]);

    const handleYearChange = (e) => {
        preloadModelsForYear(e.target.value);
        setDisabledMakes(true);
        setDisabledModels(true);
        setTrims([]);
        setShowTrims(false);
        setCurrentAppData((prevState) => {
            return {
                ...prevState,
                year: e.target.value,
                make: "",
                model: "",
                trim: "",
                cab: "",
                has_trims: false,
                has_cabs: false,
                check: undefined,
                step: 1,
                configurator_complete: undefined,
                biz2_coverages: null,
                customRearAddons: []
            };
        });
    };

    const handleMakeChange = (e) => {
        setTrims([]);
        setShowTrims(false);
        setCurrentAppData((prevState) => {
            return {
                ...prevState,
                make: e.target.value,
                model: "",
                trim: "",
                cab: "",
                has_trims: false,
                has_cabs: false,
                check: undefined,
                step: 1,
                configurator_complete: undefined,
                biz2_coverages: null,
                customRearAddons: []
            };
        });
    };

    async function runCheckNow(
        value: string | undefined,
        trim: string | undefined,
        has_trims: boolean,
    ) {
        const check = await runCheck(value, trim);
        // console.log('runCheckNow', value, trim, has_trims)
        // console.log('runCheckNow trims', trims);
        // console.log('runCheckNow trims', getTrims(value).length > 0, hasCabSizes(value));

        if (
            check.compatibility_custom !== "N" &&
            window.scs_force_normal !== true &&
            window.is_real_truck !== true
        ) {
            //alert('Need to get custom product via graphql and show the options for custom product');
            if (window.scs_force_custom !== true) {
                const product = await getCustomProduct(value, "");
                setCurrentAppData((prevState) => {
                    return {
                        ...prevState,
                        product: product,
                        check: check,
                        trim: trim,
                        has_trims: getTrims(currentAppData?.trims, value).length > 0,
                        has_cabs: getCabSizes(value, trim).length > 0,
                    };
                });
            }
            //product.images = currentAppData?.originalProduct?.images;
            setCurrentAppData((prevState) => {
                return {
                    ...prevState,
                    check: check,
                    trim: trim,
                    has_trims: getTrims(currentAppData?.trims, value).length > 0,
                    has_cabs: getCabSizes(value, trim).length > 0,
                };
            });
            return;
        }
        setCurrentAppData((prevState) => {
            return {
                ...prevState,
                check: check,
                trim: trim,
                has_trims: getTrims(currentAppData?.trims, value).length > 0,
                has_cabs: getCabSizes(value, trim).length > 0,
            };
        });
        return;
    }

    function hasCabSizes(model) {
        if (typeof trims[model] === "undefined") {
            return false;
        }
        const cabs = [
            ...new Set(
                trims[model].map((item) => item.cab)
                    .filter((item) => item.trim() != ""),
            ),
        ];
        if (cabs.length == 1 && cabs[0].trim() === "") {
            return false;
        }
        return (cabs.length > 0);
    }

    function getCabSizes(model, trim) {
        if (typeof trims[model] === "undefined") {
            return [];
        }
        return [
            ...new Set(
                trims[model]
                    .filter((item) => item.trim == trim)
                    .map((item) => item.cab.trim()).filter((item) => item.trim() != ""),
            ),
        ];
    }

    async function runCheckNowWithCabSize(
        value: string | undefined,
        trim: string | undefined,
        cabSize: string | undefined,
        has_trims: boolean,
        has_cab_sizes: boolean,
        year = currentAppData?.year,
        make = currentAppData?.make,
    ) {
        const check = await runCheck(value, trim, cabSize, year, make);
        if (
            check.compatibility_custom !== "N" &&
            window.scs_force_normal !== true &&
            window.is_real_truck !== true
        ) {
            //alert('Need to get custom product via graphql and show the options for custom product');
            if (window.scs_force_custom !== true) {
                const product = await getCustomProduct(value, "");
                setCurrentAppData((prevState) => {
                    return {
                        ...prevState,
                        product: product,
                        check: check,
                        trim: trim,
                        has_trims: has_trims,
                        cab: cabSize,
                        has_cabs: hasCabSizes(currentAppData.model),
                    };
                });
            }
            //product.images = currentAppData?.originalProduct?.images;
            setCurrentAppData((prevState) => {
                return {
                    ...prevState,
                    check: check,
                    trim: trim,
                    has_trims: has_trims,
                    cab: cabSize,
                    has_cabs: hasCabSizes(currentAppData.model),
                };
            });
            return check;
        }
        setCurrentAppData((prevState) => {
            return {
                ...prevState,
                check: check,
                trim: trim,
                has_trims: has_trims,
                cab: cabSize,
                has_cabs: hasCabSizes(currentAppData.model),
            };
        });
        return check;
    }

    async function runTrims(mm) {
        // console.log('mm:', mm);
        if (trims && typeof trims[mm] !== "undefined" && trims[mm].length === 1) {
            await runCheckNow(mm, trims[mm][0].trim, true);
            // Only one trim — it was auto-selected, so allow advancing to step 2
            setCanMoveToStep2(true);
        }
    }


    useEffect(() => {
        if (!canMoveToStep2) return;
        const timerId = setTimeout(function () {
            // Do not auto-advance when the availability API says trim must be
            // selected first — use ref to read the live value, not the stale closure.
            if (showTrimCabRef.current) return;
            // Only move to step 2 (not 3) automatically. We should not skip
            // directly to step 3, and we must have a compatibility check
            // that confirms availability before enabling the next steps.
            if (!hasAvailableSeats(currentAppData?.check)) {
                // do not auto-advance if there's no confirmed availability
                return;
            }

            const newStep = 2;
            setCurrentAppData((prevState) => {
                // Don't regress a step the user (or automated vehicle-prefill) has
                // already moved past — this timeout can still fire after later steps
                // (seat, configurator, color) have already completed.
                if ((prevState.step ?? 0) > newStep) return prevState;
                return {...prevState, step: newStep};
            });
        }, 1000);
        return () => clearTimeout(timerId);
    }, [canMoveToStep2]);

    async function runHandleModelChange(value, isUserAction = false) {
        // Reactive effects call this whenever model or trims data changes (e.g. when the
        // modelsQuery resolves after the auto-advance has already moved to step 2).
        // Guard against those late-firing effects resetting the stepper back to step 1.
        if (!isUserAction && currentStepRef.current > 1) return;
        // Also skip while a typeahead pick is being applied — trim/cab were just
        // set to a value that matches this exact model, so they must not be reset.
        if (!isUserAction && typeaheadSelectionRef.current) return;

        // Deep-linked vehicles (Product.tsx sets year+make+model+trim+cab together from
        // the query string, e.g. ?year=&make=&model=&trim=&cab=, or from the vehicle-slug
        // lookup) already carry a trim/cab matching this exact model. This effect normally
        // resets trim/cab whenever model "changes", assuming a real model switch needs
        // trim/cab re-picked — but here the model hasn't actually changed, this call is
        // just the effect re-firing for the model deep linking already populated, so the
        // reset must be skipped (same rationale as typeaheadSelectionRef above, but for
        // the non-typeahead deep-link path).
        const deepLinkedTrim = (!isUserAction && currentAppData?.model === value) ? currentAppData?.trim : "";
        const deepLinkedCab = (!isUserAction && currentAppData?.model === value) ? currentAppData?.cab : "";

        setShowTrims(!!deepLinkedTrim);
        await setCurrentAppData((prevState) => {
            return {
                ...prevState, check: undefined, step: 1, model: value,
                trim: deepLinkedTrim, cab: deepLinkedCab, has_trims: false,
                has_cabs: false, configurator_complete: undefined, configurator_has_questions: undefined, biz2_coverages: null,
                customRearAddons: []
            };
        });

        if (currentAppData?.year && currentAppData?.make && value) {
            /*if (
              window.location.href.includes("seatcoversolutions.com") &&
              !window.location.href.includes("/pages/jeep-landing-page") &&
              currentAppData?.make === "Jeep" &&
              ["Wrangler 2-Door", "Wrangler 4-Door"].includes(value)
            ) {
              window.location.href = `https://www.seatcoversolutions.com/pages/jeep-landing-page?make=${currentAppData?.make}&model=${value}&year=${currentAppData?.year}`;
              return;
            }*/

            // console.log("trims", trims);

            //DONE: Run compat check on backend and save compatibility information
            // console.log(
            //     "trims check",
            //     value,
            //     typeof trims[value] !== "undefined",
            //     trims[value],
            // );
            if (
                trims &&
                typeof trims[value] !== "undefined" &&
                trims[value].length > 0
            ) {
                setCurrentAppData((prevState) => {
                    const ttrims = getTrims(currentAppData?.trims, value);

                    return {
                        ...prevState,
                        has_trims: ttrims.length > 0,
                        has_cabs: hasCabSizes(value),
                    };
                });
                setShowTrims(true);

                if (deepLinkedTrim) {
                    await runCabSelectionSideEffects(value, deepLinkedTrim, deepLinkedCab);
                    setCanMoveToStep2(true);
                } else {
                    setTimeout(async function () {
                        await runTrims(value);
                    }, 10);
                }
            } else {
                await runCheckNow(value, deepLinkedTrim, !!deepLinkedTrim);
            }
            return;
        }

        setCurrentAppData((prevState) => {
            return {...prevState, check: undefined, trim: "", has_trims: false};
        });
    }

    const handleModelChange = async (e) => {
        runHandleModelChange(e.target.value, true);
    };

    useEffect(() => {
        function runLocal() {
            if (currentAppData?.model && currentAppData?.trims) {
                setTrims(currentAppData?.trims);
                // console.log('currentAppData?.trims:', currentAppData?.model, currentAppData?.trims);
                setShowTrims(
                    currentAppData?.trims &&
                    currentAppData?.model &&
                    currentAppData?.trims[currentAppData?.model] &&
                    currentAppData?.trims[currentAppData?.model].length > 0,
                );
                runHandleModelChange(currentAppData?.model);
            }
        }
        const timerId = setTimeout(runLocal, 200);
        return () => clearTimeout(timerId);
    }, [currentAppData?.model]);

    // Shared by handleCabSizeChange (manual pick) and handleTrimChange (when a
    // trim has exactly one cab option and it gets auto-selected — in that case
    // the <select> already displays the sole option, so no native change event
    // ever fires for it and this logic must be run explicitly instead).
    async function runCabSelectionSideEffects(
        model,
        trim,
        cabValue,
        year = currentAppData?.year,
        make = currentAppData?.make,
    ) {
        const check = await runCheckNowWithCabSize(model, trim, cabValue, true, true, year, make);

        // When show_trim_cab is active, fetch availability for this specific
        // YMM+trim+cab to decide whether configurator questions apply.
        if (showTrimCabRef.current && trim && cabValue) {
            try {
                const trimCabData = await fetchVehicleAvailabilityWithTrimCab(
                    year ?? '', make ?? '', model ?? '',
                    trim, cabValue,
                );
                const cfg = Array.isArray((trimCabData as any)?.data?.configurator)
                    ? (trimCabData as any).data.configurator : [];
                const hasQ = cfg.length > 0 &&
                    Array.isArray(cfg[0]?.questions) &&
                    cfg[0].questions.length > 0;
                // For trim_cab_only, questions are always skipped regardless of
                // whether questions exist in the payload.
                const trimCabDisplayMode = cfg[0]?.display_mode ?? 'icon_path';
                const effectivelyNoQuestions = !hasQ || trimCabDisplayMode === 'trim_cab_only';
                // When no questions, build biz2_coverages from the first SKU mapping's
                // availability for this exact trim/cab. When questions exist, clear it
                // so the configurator rebuilds it after the user answers.
                const firstMappingAvailability = effectivelyNoQuestions
                    ? (cfg[0]?.sku_mappings?.[0]?.availability ?? null)
                    : null;
                const biz2_coverages = firstMappingAvailability
                    ? buildBiz2Coverages(firstMappingAvailability)
                    : null;

                setCurrentAppData(prev => ({
                    ...prev,
                    configurator_has_questions: !effectivelyNoQuestions,
                    biz2_coverages,
                }));
            } catch {
                // On error default to showing questions (safe fallback).
                setCurrentAppData(prev => ({ ...prev, configurator_has_questions: true }));
            }
        }

        return check;
    }

    // True when a compatibility check response confirms at least one
    // seat/color combination is actually available.
    function hasAvailableSeats(check) {
        if (!check) return false;
        try {
            const availability = check.availability || {};
            return Object.values(availability).some((seatObj) =>
                Object.values((seatObj as any) || {}).some((v) => v === true || v === 'true')
            );
        } catch (e) {
            return false;
        }
    }

    // Handles a vehicle picked from the YMM+trim+cab typeahead (window.scs_autocomplete_ymm).
    // The typeahead already resolves a full year/make/model/trim/cab in one shot, so — unlike
    // the incremental selects — we run the compatibility check immediately and, if it confirms
    // availability, advance straight to step 2 rather than waiting for a manual Next click.
    const handleTypeaheadSelect = async (vehicle: TypeaheadVehicle) => {
        const year = String(vehicle.year);
        const make = vehicle.make;
        const model = vehicle.model;
        const trim = vehicle.trim ?? "";
        const cab = vehicle.cab ?? "";

        typeaheadSelectionRef.current = true;
        try {
            setCurrentAppData((prevState) => {
                return {
                    ...prevState,
                    check: undefined,
                    step: 1,
                    year,
                    make,
                    model,
                    trim,
                    cab,
                    has_trims: !!trim,
                    has_cabs: !!cab,
                    configurator_has_questions: undefined,
                };
            });

            const check = await runCabSelectionSideEffects(model, trim, cab, year, make);

            if (hasAvailableSeats(check)) {
                setCurrentAppData((prev) => ({ ...prev, step: 2 }));
            }
        } finally {
            typeaheadSelectionRef.current = false;
        }
    };

    const handleTrimChange = async (e) => {
        const cabSizes = getCabSizes(currentAppData?.model, e.target.value);
        setCurrentAppData((prevState) => {

            // console.log(
            //     "handleTrimChange",
            //     e.target.value,
            //     cabSizes,
            //     cabSizes.length,
            // );
            return {
                ...prevState, check: undefined,
                step: 1,
                trim: e.target.value,
                has_trims: getTrims(currentAppData?.trims, currentAppData?.model).length > 0,
                has_cabs: cabSizes.length > 0,
                cab: cabSizes.length == 1 ? cabSizes[0] : "",
                configurator_has_questions: undefined,
            };
        });
        // When show_trim_cab is active and the model has cab options, do NOT
        // run the check immediately — the user must also select a cab.
        // When there are genuinely no cabs (hasCabSizes returns false) we still
        // run the check so StepNav can enable Next.
        const modelHasCabs = showTrimCabRef.current && hasCabSizes(currentAppData?.model);
        if (cabSizes.length <= 1 && !modelHasCabs) {
            await runCheckNow(currentAppData?.model, e.target.value, true);
        } else if (modelHasCabs && cabSizes.length === 1) {
            // Sole cab option was auto-selected above (e.g. TRX's only "Crew Cab").
            // The cab <select> won't fire a change event for a value it already
            // displays, so run the same side effects a manual pick would trigger.
            await runCabSelectionSideEffects(currentAppData?.model, e.target.value, cabSizes[0]);
        }
        //
        // const check = await runCheck(currentAppData?.model, e.target.value);
        // if (check.compatibility === 'N') {
        //     //alert('Need to get custom product via graphql and show the options for custom product');
        //     const product = await getCustomProduct(currentAppData?.model, e.target.value);
        //     product.images = currentAppData?.originalProduct?.images;
        //     await setCurrentAppData(prevState => {
        //         return { ...prevState, product: product }
        //     });
        //     return;
        // }
    };
    const handleCabSizeChange = async (e) => {
        const cabValue = e.target.value;
        const model = currentAppData?.model;
        const trim = currentAppData?.trim;

        // Immediately update cab; clear stale check and questions flag.
        setCurrentAppData((prevState) => {
            return {
                ...prevState,
                cab: cabValue,
                check: undefined,
                configurator_has_questions: undefined,
            };
        });

        await runCabSelectionSideEffects(model, trim, cabValue);
    };

    const handleClickChange = (e) => {
        setTrims([]);
        setShowTrims(false);
        setCurrentAppData((prevState) => {
            return {
                ...prevState,
                step: 1,
                // year: "",
                // make: "",
                model: "",
                trim: "",
                cab: "",
                has_trims: false,
                has_cabs: false,
                seat: "",
                color: "Black",
                addons: "",
                check: undefined,
                customRearAddons: [],
                configurator_complete: false,
                sku_mapping_availability: undefined,
                biz2_coverages: null,
            };
        });
    };

    const runCheck = async (model, trim, cabSize = "", year = currentAppData?.year, make = currentAppData?.make) => {
        setCurrentAppData((prevState) => ({ ...prevState, isFetchingCheck: true }));
        try {
            const check = await fetchCheck(
                year,
                make,
                model,
                trim,
                currentAppData?.color,
                currentAppData?.seat,
                cabSize,
            );
        // console.log(`check result ${currentAppData?.year} ${currentAppData?.make} ${model} ${trim}`, check);
        let newStep = currentAppData?.step;
            if (
                (check.compatibility === "A" || check.compatibility === "N") &&
                check.compatibility_custom === "N"
            ) {
                newStep = 3;
                await setCurrentAppData((prevState) => {
                    return {
                        ...prevState,
                        check: check,
                        trim: trim,
                        step: newStep,
                        has_cabs: hasCabSizes(currentAppData.model),
                        isFetchingCheck: false,
                    };
                });
                return check;
            }
            await setCurrentAppData((prevState) => {
                return {
                    ...prevState,
                    check: check,
                    trim: trim,
                    has_cabs: hasCabSizes(currentAppData.model),
                    isFetchingCheck: false,
                };
            });
            return check;
        } catch (e) {
            setCurrentAppData((prevState) => ({ ...prevState, isFetchingCheck: false }));
            throw e;
        }
    };

    const getCustomProduct = (value: string | undefined, trim: string) => {
        custom_refetch();
        // console.log('getCustomProduct data', custom_data);

        return custom_data;
    };

    useEffect(() => {
        if (!currentAppData?.model) return;

        // console.log('useEffect model');

        async function runEffect() {
            if (
                currentAppData?.year &&
                currentAppData?.make &&
                currentAppData?.model
            ) {
                await runHandleModelChange(currentAppData?.model);

                if (currentAppData?.has_trims) {
                    // console.log('test: ', typeof trims[currentAppData.model] !== 'undefined' && trims[currentAppData?.model].length > 0);
                    setShowTrims(
                        typeof trims[currentAppData.model] !== "undefined" &&
                        trims[currentAppData?.model].length > 0,
                    );
                } else {
                    // run check
                    //            runCheckNow(currentAppData?.model, '', false);
                }
                return;
            }
            setCurrentAppData((prevState) => {
                return {...prevState, check: undefined, trim: "", has_trims: false};
            });
        }

        runEffect();
    }, [currentAppData?.model, trims]);

    /*useEffect(() => {
          if (!currentAppData?.trim || currentAppData?.trim === '')
              return;

          runCheckNow(currentAppData?.model, currentAppData?.trim, true);

      }, [currentAppData?.trim]);*/

    // console.log('trims', trims, currentAppData?.model, showTrims, trims && currentAppData?.model && showTrims)

    // console.log(
    //     'makesQuery', makesQuery, makesQuery?.data, disabledMakes
    // );

    //console.log('TRIMS:', trims);

    return (<>
            <SuperOffer />
            <section
                data-testid="step1new-root"
                className={`${styles.section__step} ${window.scs_custom_style ? styles.style01 : ""} ${currentAppData?.step == 1 ? styles.section_active : ""} 
      ${window.scs_black_friday_2025 ? styles.has_bf_2025 : ''} ${window.scs_new_design_2026 ? styles.scs_new_design_2026 : ''}`}
                id="step_1"
            >
                <h2>
                    <span className={`${styles.section__step_no} ${currentAppData?.step !== 1 ? styles.section__step_no_done : ''}`}>{props.stepNumber ?? 1}</span>
                </h2>

                {(availabilityLoading && availEnabled) || currentAppData?.isFetchingCheck && (
                    <div className="overlay_loader" role="status" aria-live="polite">
                        <div className="overlay_loader_inner">
                            <span className="overlay_spinner" aria-hidden="true"></span>
                            <span className="overlay_message">Loading vehicle configuration...</span>
                        </div>
                    </div>
                )}


                {currentAppData?.step !== 1 ? (
                    <div className={`${styles.selector_wrapper}`}>
                        <strong><span className={`${styles.section__step_no}`}>{props.stepNumber ?? 1}</span>Vehicle Details</strong>
                        <div className={`${styles.summary}`}>
                            <div className={`${styles.selected_value}`}>{`${currentAppData?.year ?? ''} ${currentAppData?.make ?? ''} ${currentAppData?.model ?? ''}${currentAppData?.trim ? ' ' + currentAppData.trim : ''}${currentAppData?.cab ? ' ' + currentAppData.cab : ''}`}</div>
                            <button type="button" className={`${styles.btn_change}`} onClick={handleClickChange} aria-label="Edit vehicle selection">
                                <svg width="14" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M0.274589 0.274547C0.640708 -0.0915156 1.2343 -0.0915156 1.60041 0.274547L7.1875 5.86167L12.7746 0.274547C13.1407 -0.0915156 13.7343 -0.0915156 14.1004 0.274547C14.4665 0.640672 14.4665 1.2343 14.1004 1.60042L7.85044 7.85042C7.48431 8.21648 6.89069 8.21648 6.52456 7.85042L0.274589 1.60042C-0.0915297 1.2343 -0.0915297 0.640672 0.274589 0.274547Z" fill="black"/></svg>
                            </button>
                        </div>
                        {!window.scs_force_custom && currentAppData.check && currentAppData.check?.alert ? (
                            <div className={`${styles.vehicle_alert}`}>
                                <strong>Warning:</strong> {currentAppData.check?.alert}
                            </div>
                        ) : (
                            ""
                        )}
                    </div>
                ) : (<>

                    <div className={`${styles.selector_wrapper}`}>
                        <strong><span className={`${styles.section__step_no}`}>{props.stepNumber ?? 1}</span>Vehicle Details</strong>

                        {window.scs_autocomplete_ymm ? (
                            <VehicleTypeahead id={props.id} onSelect={handleTypeaheadSelect} />
                        ) : (
                        <div
                            className={`${styles.vehicle_selector} ${showTrims && (!hasConfiguratorQuestions || showTrimCab) ? styles.has_trims : ""}`}
                        >

                            <div className={`${styles.select_wrapper}`}>
                                <label className={styles.sr_only} htmlFor={`${props.id}-vehicle-year`} style={visuallyHiddenStyle}>
                                    Vehicle year
                                </label>
                                <select
                                    id={`${props.id}-vehicle-year`}
                                    name="vehicle-year"
                                    onChange={handleYearChange}
                                    value={currentAppData?.year}
                                    data-selected={currentAppData?.year}
                                >
                                    <option value="">Year</option>
                                    {yearsQuery.data &&
                                        yearsQuery.data.map((year, i) => {
                                            return (
                                                <option
                                                    key={`opt-${props.id}-${year}`}
                                                    value={`${slugify(year)}`}

                                                >
                                                    {year}
                                                </option>
                                            );
                                        })}
                                </select>
                            </div>
                            <div className={`${styles.select_wrapper}`}>
                                <label className={styles.sr_only} htmlFor={`${props.id}-vehicle-make`} style={visuallyHiddenStyle}>
                                    Vehicle make
                                </label>
                                <select
                                    id={`${props.id}-vehicle-make`}
                                    name="vehicle-make"
                                    onChange={handleMakeChange}
                                    disabled={disabledMakes}
                                    value={currentAppData?.make}
                                    data-selected={currentAppData?.make}
                                >
                                    <option value="">Make</option>
                                    {window.scs_use_optgroup ? (
                                        <>
                                            <optgroup label="Top Sellers">
                                                {makesQuery &&
                                                    makesQuery.data &&
                                                    makesQuery.data
                                                        .filter((v) => v.trim() != "")
                                                        .slice(0, 6)
                                                        .map((make, i) => {
                                                            return (
                                                                <option
                                                                    key={`opt-${slugify(make)}`}
                                                                    value={`${make}`}

                                                                >
                                                                    {make}
                                                                </option>
                                                            );
                                                        })}
                                            </optgroup>
                                            <optgroup label="All Others">
                                                {makesQuery &&
                                                    makesQuery.data &&
                                                    makesQuery.data
                                                        .filter((v) => v.trim() != "")
                                                        .slice(6)
                                                        .map((make, i) => {
                                                            return (
                                                                <option
                                                                    key={`opt-${props.id}-${slugify(make)}`}
                                                                    value={`${make}`}

                                                                >
                                                                    {make}
                                                                </option>
                                                            );
                                                        })}
                                            </optgroup>
                                        </>
                                    ) : (
                                        makesQuery &&
                                        makesQuery.data &&
                                        makesQuery.data.map((make, i) => {
                                            return (
                                                <option
                                                    key={`opt-${slugify(make)}`}
                                                    value={`${make}`}

                                                >
                                                    {make}
                                                </option>
                                            );
                                        })
                                    )}
                                </select>
                            </div>
                            <div className={`${styles.select_wrapper}`}>
                                <label className={styles.sr_only} htmlFor={`${props.id}-vehicle-model`} style={visuallyHiddenStyle}>
                                    Vehicle model
                                </label>
                                <select
                                    id={`${props.id}-vehicle-model`}
                                    name="vehicle-model"
                                    onChange={handleModelChange}
                                    disabled={disabledModels}

                                    value={currentAppData?.model}
                                >
                                    <option value="">Model</option>
                                    {window.scs_use_optgroup && MODEL_SORT_ORDER[currentAppData?.make] ? (
                                        <>
                                            <optgroup label="Top Sellers">
                                                {modelsQuery &&
                                                    modelsQuery.data &&
                                                    modelsQuery.data.slice(0, Object.keys(MODEL_SORT_ORDER[currentAppData?.make]).length).map((model, i) => {
                                                        return (
                                                            <option
                                                                key={`opt-${slugify(model)}`}
                                                                value={`${model}`}

                                                            >
                                                                {model}
                                                            </option>
                                                        );
                                                    })}
                                            </optgroup>
                                            <optgroup label="All Others">
                                                {modelsQuery &&
                                                    modelsQuery.data &&
                                                    modelsQuery.data.slice(Object.keys(MODEL_SORT_ORDER[currentAppData?.make]).length).map((model, i) => {
                                                        return (
                                                            <option
                                                                key={`opt-${slugify(model)}`}
                                                                value={`${model}`}

                                                            >
                                                                {model}
                                                            </option>
                                                        );
                                                    })}
                                            </optgroup>
                                        </>
                                    ) : (
                                        modelsQuery &&
                                        modelsQuery.data &&
                                        modelsQuery.data.map((model, i) => {
                                            return (
                                                <option
                                                    key={`opt-${slugify(model)}`}
                                                    value={`${model}`}

                                                >
                                                    {model}
                                                </option>
                                            );
                                        })
                                    )}
                                </select>
                            </div>
                            {trims && currentAppData?.model && showTrims && !availabilityLoading && (!hasConfiguratorQuestions || showTrimCab) ? (
                                <div className={`${styles.select_wrapper}`}>
                                    <label className={styles.sr_only} htmlFor="vehicle-trim" style={visuallyHiddenStyle}>
                                        Vehicle trim
                                    </label>
                                    <select id={`vehicle-trim`} onChange={handleTrimChange}
                                            value={currentAppData?.trim}>
                                        <option value="">Trim</option>
                                        {trims &&
                                            typeof trims[currentAppData?.model] !== "undefined" &&
                                            getTrims(trims, currentAppData?.model).map((obj, i) => {
                                                const trim = obj;
                                                return (
                                                    <option
                                                        key={`opt-${slugify(trim)}`}
                                                        value={`${trim}`}

                                                    >
                                                        {trim}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </div>
                            ) : (
                                ""
                            )}
                            {trims &&
                            currentAppData.model &&
                            showTrims &&
                            !availabilityLoading &&
                            (!hasConfiguratorQuestions || showTrimCab) &&
                            hasCabSizes(currentAppData.model) &&
                            typeof trims[currentAppData.model] !== "undefined" &&
                            getCabSizes(currentAppData?.model, currentAppData?.trim).length > 0 ? (
                                <div className={`${styles.select_wrapper}`}>
                                    <label className={styles.sr_only} htmlFor="vehicle-cab-size" style={visuallyHiddenStyle}>
                                        Vehicle cab size
                                    </label>
                                    <select id={`vehicle-cab-size`} onChange={handleCabSizeChange}
                                            value={currentAppData?.cab}>
                                        <option value="">Cab Size</option>
                                        {trims &&
                                            typeof trims[currentAppData.model] !== "undefined" &&
                                            getCabSizes(currentAppData?.model, currentAppData?.trim).map(
                                                (cabSize, i) => {
                                                    return (
                                                        <option
                                                            key={`opt-${slugify(cabSize)}`}
                                                            value={`${cabSize}`}

                                                        >
                                                            {cabSize}
                                                        </option>
                                                    );
                                                },
                                            )}
                                    </select>
                                </div>
                            ) : (
                                ""
                            )}
                        </div>
                        )}
                        {!window.scs_force_custom && currentAppData.check && currentAppData.check?.alert ? (
                            <div className={`${styles.vehicle_alert}`}>
                                <strong>Warning:</strong> {currentAppData.check?.alert}
                            </div>
                        ) : (
                            ""
                        )}
                        {/*{window.scs_step_1_bottom_text ? (
            <div
              className={`${styles.bottom_text}`}
              dangerouslySetInnerHTML={{
                __html: `${window.scs_step_1_bottom_text}`,
              }}
            ></div>
          ) : (
            ""
          )}*/}
                    </div>
                </>)}

            </section>
        </>
    );
}
